<template>
  <div>
    <authentication-modal v-bind="authenticationModalProps" @authenticated="emitAuthenticated"
                          @awaitingEmailConfirmation="awaitingEmailConfirmation"
                          @errorOnSignUp="setNonRecoverableErrorOnSignup"
                          @mfa-token-required="mfaTokenRequired"
                          @missing-info-required="missingInfoRequired"
                          @modal-closed="authenticationModalClosed">
      <template #header>
        <h2 class="mt-0">
          {{ title }}
        </h2>
        <p>{{ description }}</p>
      </template>
    </authentication-modal>

    <confirm-your-email-modal v-if="displayUserAccountCreationAwaitingEmailConfirmation"
                              :email-sent-message="i18n.t('member.account.confirm_your_email_modal.sent_for_user')"
                              :warning-message="confirmationWarningMessage"
                              :email-address="userEmailAddressAwaitingConfirmation" />

    <generic-error-modal v-if="nonRecoverableErrorOnSignup" :error-message="nonRecoverableErrorOnSignup" />

    <verify-mfa-token-modal v-if="displayVerifyMfaTokenModal" v-bind="mfaParameters" @authenticated="emitAuthenticated"
                            @authenticity-token-updated="authenticityTokenUpdated"
                            @missing-info-required="missingInfoRequired" />

    <missing-info-modal v-if="displayMissingInfoModal" entity-name="user" :required-fields="missingInfoRequiredFields"
                        :eu-data-processing-consent-data="authenticationModalData.euDataProcessingConsentData"
                        :additional-field-configs="authenticationModalData.additionalFieldConfigs"
                        :phone-number-label="authenticationModalData.phoneNumberLabel"
                        :postcode-label="authenticationModalData.postcodeLabel"
                        @completed="emitAuthenticated" />
  </div>
</template>

<script>
import AuthenticationModal from './authentication-modal'
import ConfirmYourEmailModal from '@/components/confirm-your-email-modal'
import GenericErrorModal from '@/components/generic-error-modal.vue'
import { inject } from 'vue'
import MissingInfoModal from './missing-info-modal'
import VerifyMfaTokenModal from './verify-mfa-token-modal'

export default {
  components: { AuthenticationModal, ConfirmYourEmailModal, GenericErrorModal, MissingInfoModal, VerifyMfaTokenModal },

  props: {
    authenticationModalData: {
      type: Object,
      required: true
    },
    closeable: {
      type: Boolean,
      required: false,
      default: false
    },
    confirmationWarningMessage: {
      type: String,
      required: false,
      default() { return inject('i18n').t('member.account.authenticate_to_continue.email_confirmation_warning') }
    },
    description: {
      type: String,
      required: true
    },
    signInButtonText: {
      type: String,
      required: false,
      default() { return inject('i18n').t('member.account.authentication_modal.sign_in') }
    },
    signUpButtonText: {
      type: String,
      required: false,
      default() { return inject('i18n').t('member.account.authentication_modal.sign_up_submit') }
    },
    title: {
      type: String,
      required: false,
      default() { return inject('i18n').t('member.account.authenticate_to_continue.title') }
    }
  },

  emits: ['authenticated', 'modal-closed', 'user-awaiting-confirmation'],

  data() {
    return {
      displayMissingInfoModal: false,
      displayUserAccountCreationAwaitingEmailConfirmation: false,
      displayVerifyMfaTokenModal: false,
      mfaParameters: null,
      nonRecoverableErrorOnSignup: null,
      userEmailAddressAwaitingConfirmation: null,
      validationErrors: {}
    }
  },

  computed: {
    authenticationModalProps() {
      return {
        additionalFieldConfigs: this.authenticationModalData.additionalFieldConfigs,
        askForCountry: this.authenticationModalData.askForCountry,
        askForPostcode: this.authenticationModalData.askForPostcode ,
        askForSmsOptIn: this.authenticationModalData.askForSmsOptIn,
        closeable: this.closeable,
        countryData: this.authenticationModalData.countryData,
        emailOptInLabel: this.authenticationModalData.emailOptInLabel,
        emailOptInTypeData: this.authenticationModalData.emailOptInTypeData,
        enableFacebookLogin: this.authenticationModalData.enableFacebookLogin ,
        enableGoogleLogin: this.authenticationModalData.enableGoogleLogin,
        euDataProcessingConsentData: this.authenticationModalData.euDataProcessingConsentData,
        facebookLoginUrl: this.authenticationModalData.facebookLoginUrl,
        googleLoginUrl: this.authenticationModalData.googleLoginUrl,
        newAccountDisclaimer: this.authenticationModalData.newAccountDisclaimer,
        organisationId: this.authenticationModalData.organisationId,
        phoneNumberLabel: this.authenticationModalData.phoneNumberLabel,
        phoneNumberMandatory: this.authenticationModalData.phoneNumberMandatory,
        postcodeLabel: this.authenticationModalData.postcodeLabel,
        showEuDataProcessingConsent: this.authenticationModalData.showEuDataProcessingConsent,
        signInButtonText: this.signInButtonText,
        signUpButtonText: this.signUpButtonText,
        smsOptInLabel: this.authenticationModalData.smsOptInLabel,
        smsOptInTypeData: this.authenticationModalData.smsOptInTypeData,
        termsOfServiceData: this.authenticationModalData.termsOfServiceData,
        validationErrors: this.validationErrors
      }
    }
  },

  methods: {
    authenticationModalClosed() {
      // Only emit modal-closed if the modal was closed by the user, not if the original
      // authentication modal was closed in favor of a different modal.
      if (!this.displayUserAccountCreationAwaitingEmailConfirmation &&
          !this.nonRecoverableErrorOnSignup &&
          !this.displayVerifyMfaTokenModal &&
          !this.displayMissingInfoModal) {
        this.$emit('modal-closed')
      }
    },

    authenticityTokenUpdated(newAuthenticityToken) {
      const tokenElement = document.querySelector('meta[name="csrf-token"]')
      tokenElement.setAttribute('content', newAuthenticityToken)
    },

    awaitingEmailConfirmation({ userEmail, userToken }) {
      this.displayUserAccountCreationAwaitingEmailConfirmation = true
      this.userEmailAddressAwaitingConfirmation = userEmail
      this.$emit('user-awaiting-confirmation', userToken)
    },

    emitAuthenticated() {
      this.$emit('authenticated')
    },

    mfaTokenRequired(mfaParameters) {
      this.displayVerifyMfaTokenModal = true
      this.mfaParameters = mfaParameters
    },

    missingInfoRequired({ requiredFields }) {
      this.displayMissingInfoModal = true
      this.missingInfoRequiredFields = requiredFields
    },

    setNonRecoverableErrorOnSignup(errorMessage) {
      this.nonRecoverableErrorOnSignup = errorMessage
    }
  }
}
</script>
